/**
 * How an answer relates to the conversation, as the runtime reported it.
 *
 * Arrives as the ANSWER_CONTEXT custom event while conversation memory is on.
 * It names certified members and phrases, never result values, so it is safe to
 * show and to keep. Read defensively: an event this client cannot read is
 * dropped rather than half-rendered, and the answer itself is unaffected.
 */

export type TurnRelation =
  | "new"
  | "refine"
  | "drill"
  | "compare"
  | "rechart"
  | "expand"
  | "explain";

export type ChangeField =
  | "metrics"
  | "dimensions"
  | "filters"
  | "period"
  | "chart"
  | "limit";

export interface AnswerChange {
  field: ChangeField;
  before: string[];
  after: string[];
}

export interface AnswerContext {
  summary: string;
  frameId: string;
  relation: TurnRelation;
  /** One line saying what the question was understood as. */
  interpretation: string;
  /** What differs from the previous answer. Empty for a new question. */
  changes: AnswerChange[];
}

const RELATIONS: readonly TurnRelation[] = [
  "new", "refine", "drill", "compare", "rechart", "expand", "explain",
];
const FIELDS: readonly ChangeField[] = [
  "metrics", "dimensions", "filters", "period", "chart", "limit",
];
// The published contract's bounds, applied again here so a misbehaving
// producer cannot make the strip unreadable.
const MAX_INTERPRETATION = 300;
const MAX_CHANGES = 12;
const MAX_VALUES = 24;
const MAX_VALUE_LENGTH = 200;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown, limit: number): string {
  return typeof value === "string" ? value.trim().slice(0, limit) : "";
}

function values(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string" && item.trim() !== "")
    .slice(0, MAX_VALUES)
    .map((item) => item.trim().slice(0, MAX_VALUE_LENGTH));
}

function change(value: unknown): AnswerChange | null {
  if (!isRecord(value)) return null;
  const field = value.field;
  if (typeof field !== "string" || !FIELDS.includes(field as ChangeField)) {
    return null;
  }
  return {
    field: field as ChangeField,
    before: values(value.before),
    after: values(value.after),
  };
}

/** The event's value as an answer context, or null when it cannot be read. */
export function readAnswerContext(value: unknown): AnswerContext | null {
  if (!isRecord(value)) return null;
  const relation = value.relation;
  if (typeof relation !== "string" || !RELATIONS.includes(relation as TurnRelation)) {
    return null;
  }
  const frameId = text(value.frameId, 64);
  const interpretation = text(value.interpretation, MAX_INTERPRETATION);
  if (!frameId || !interpretation) return null;
  const changes = Array.isArray(value.changes)
    ? value.changes
        .map(change)
        .filter((item): item is AnswerChange => item !== null)
        .slice(0, MAX_CHANGES)
    : [];
  return {
    summary: text(value.summary, 1000),
    frameId,
    relation: relation as TurnRelation,
    interpretation,
    changes,
  };
}
